import React, { useState, useEffect } from 'react';
import { GridTableCell } from './OnlineMarketsGridViewTableCell';
import { Intents } from '../constants/order';

const lookup_month_abbr = {
    January: 'JAN',
    February: 'FEB',
    March: 'MAR',
    April: 'APR',
    May: 'MAY',
    June: 'JUN',
    July: 'JUL',
    August: 'AUG',
    September: 'SEP',
    October: 'OCT',
    November: 'NOV',
    December: 'DEC',
};

const abbrev = longPeriod => {
    if (!longPeriod) return undefined;

    return longPeriod
        .split(' ')
        .map(item => (lookup_month_abbr[item] ? item.replace(item, lookup_month_abbr[item]) : item));
};

const getPeriodForCreateOrder = longPeriod => {
    if (!longPeriod) return undefined;

    if (longPeriod === 'LOADED' || longPeriod === 'PROMPT') {
        return [longPeriod];
    }

    const splits = longPeriod.split(' ');

    if (splits.length === 3) {
        return [splits[0], lookup_month_abbr[splits[1]] ? lookup_month_abbr[splits[1]] : splits[1], splits[2]];
    }

    return ['-', lookup_month_abbr[splits[0]] ? lookup_month_abbr[splits[0]] : splits[0], splits[1]];
};

const getMaxGlowTime = contract => {
    if (Object.keys(contract).length === 0) {
        return 0;
    }

    const glowTime =
        typeof contract.updated_seconds_ago === 'number'
            ? 300 - contract.updated_seconds_ago
            : 300 - contract.created_seconds_ago;
    return glowTime;
};

const GridTableProductRow = props => {
    const {
        product,
        period,
        placeOrder,
        getOrder,
        callDH,
        toggleExpand,
        expanded,
        canExpand,
        showOrderPopup,
        isExpandGlowing,
        user,
    } = props;
    const bid = props.bid || {};
    const offer = props.offer || {};

    const expandIcon = expanded ? '-' : '+';

    const [isBidExpired, setIsBidExpired] = useState(bid.status === 'EXPIRED');
    const [isOfferExpired, setIsOfferExpired] = useState(offer.status === 'EXPIRED');
    const [isBidGlowing, setIsBidGlowing] = useState(false);
    const [isOfferGlowing, setIsOfferGlowing] = useState(false);

    const showCreateOrder = (intent = '') => {
        const period_split = getPeriodForCreateOrder(period);

        const formData = {
            trading_period: period_split[1] + ' ' + period_split[2],
            trading_period_interval: period_split[1],
            trading_period_year: period_split[2],
            trading_period_prefix: period_split[0],
            is_custom_timeframe: false,
            product_id: +product.id,
            intent,
        };

        showOrderPopup('create', formData);
    };

    const setBidExpired = () => {
        setIsBidExpired(true);
    };

    const setOfferExpired = () => {
        setIsOfferExpired(true);
    };

    useEffect(() => {
        const bidMaxGlowTime = getMaxGlowTime(bid);
        const offerMaxGlowTime = getMaxGlowTime(offer);
        let bidTimer;
        let offerTimer;

        setIsBidGlowing(bidMaxGlowTime > 0);
        setIsOfferGlowing(offerMaxGlowTime > 0);

        if (bidMaxGlowTime > 0) {
            bidTimer = setTimeout(() => setIsBidGlowing(false), 1000 * bidMaxGlowTime);
        }

        if (offerMaxGlowTime > 0) {
            offerTimer = setTimeout(() => setIsOfferGlowing(false), 1000 * offerMaxGlowTime);
        }

        return () => {
            if (bidTimer) clearTimeout(bidTimer);
            if (offerTimer) clearTimeout(offerTimer);
        };
    }, [props.bid, props.offer]);

    return (
        <div
            className="OnlineMarketsGridViewItemTableItemWrapper"
            name="order_line"
            onMouseOver={props.onMouseOver}
            onMouseOut={props.onMouseOut}
            orderid_sell={offer.intent === Intents.SELL ? offer.id : null}
            creator_company_id_sell={offer.intent === Intents.SELL ? offer.creator_company_id : null}
            price_sell={offer.intent === Intents.SELL ? offer.price : null}
            quantity_sell={offer.intent === Intents.SELL ? offer.quantity : null}
            status_sell={offer.intent === Intents.SELL ? offer.status : null}
            isexpired_sell={+isOfferExpired}
            orderid_buy={bid.intent === Intents.BUY ? bid.id : null}
            creator_company_id_buy={bid.intent === Intents.BUY ? bid.creator_company_id : null}
            price_buy={bid.intent === Intents.BUY ? bid.price : null}
            quantity_buy={bid.intent === Intents.BUY ? bid.quantity : null}
            status_buy={bid.intent === Intents.BUY ? bid.status : null}
            isexpired_buy={+isBidExpired}
            productid={product.id}
            contract_type={bid ? bid.contract_type : offer.contract_type}
            trading_period={bid ? bid.trading_period : offer.trading_period}
            currency={bid ? bid.currency_id : offer.currency_id}
        >
            <div
                className={`OnlineMarketsGridViewItemTableItem Regular ${canExpand ? 'Clickable' : ''} ${
                    isExpandGlowing ? 'Glowing' : ''
                }`}
                onClick={toggleExpand}
            >
                {abbrev(period)} {canExpand && expandIcon}
                {/* {tradingPeriodPrefix()} {abbrev(period)} {canExpand && expandIcon} */}
            </div>
            <div className="OnlineMarketsGridViewItemTableItem Regular Clickable" onClick={showCreateOrder}>
                <div className="OnlineMarketsGridViewButton Action" />
            </div>
            <div className="OnlineMarketsGridViewItemTableItem Regular Number">
                {bid.quantity && !isBidExpired ? bid.quantity : ''}
            </div>
            <GridTableCell
                price={bid.price}
                currency_id={bid.currency_id}
                columnName="Bid"
                expiry_time={bid.expiry_time}
                placeOrder={() => placeOrder(bid)}
                getOrder={() => getOrder(bid.id, 'edit', showOrderPopup)}
                showCreateOrder={() => showCreateOrder(Intents.BUY)}
                setToExpired={setBidExpired}
                isGlowing={isBidGlowing}
                intent={!isBidExpired ? bid.intent : null}
                isExpired={isBidExpired}
                isOwner={bid.creator_company_id === user.userInfo.company_id}
            />
            <GridTableCell
                price={offer.price}
                currency_id={offer.currency_id}
                columnName="Offer"
                expiry_time={offer.expiry_time}
                placeOrder={() => placeOrder(offer)}
                getOrder={() => getOrder(offer.id, 'edit', showOrderPopup)}
                showCreateOrder={() => showCreateOrder(Intents.SELL)}
                setToExpired={setOfferExpired}
                isGlowing={isOfferGlowing}
                intent={!isOfferExpired ? offer.intent : null}
                isExpired={isOfferExpired}
                isOwner={offer.creator_company_id === user.userInfo.company_id}
            />
            <div className="OnlineMarketsGridViewItemTableItem Regular Number">
                {offer.quantity && !isOfferExpired ? offer.quantity : ''}
            </div>
        </div>
    );
};

export { GridTableProductRow };



// WEBPACK FOOTER //
// ./source/components/OnlineMarketsGridViewTableProductRow.js